import type { Cell, Worksheet } from 'exceljs';

import type { RebateExportCell, RebateExportRow } from './rebateExportTypes';

export const REBATE_XLSX_SHEET_NAME = 'Report';
export const REBATE_XLSX_CURRENCY_FORMAT = '$#,##0.00;($#,##0.00)';
export const REBATE_XLSX_NUMBER_FORMAT = '0.00';
export const REBATE_XLSX_INTEGER_FORMAT = '0';
export const REBATE_XLSX_DATE_FORMAT = 'yyyy-mm-dd';
export const REBATE_XLSX_MIME_TYPE =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

function applyNumericFormat(cell: Cell, value: number, isCurrency: boolean) {
  cell.value = value;
  if (isCurrency) {
    cell.numFmt = REBATE_XLSX_CURRENCY_FORMAT;
    return;
  }
  cell.numFmt = Number.isInteger(value)
    ? REBATE_XLSX_INTEGER_FORMAT
    : REBATE_XLSX_NUMBER_FORMAT;
}

function writeExportCell(cell: Cell, exportCell: RebateExportCell) {
  if (exportCell.kind === 'empty' || exportCell.value === null) {
    cell.value = null;
    return;
  }
  if (exportCell.kind === 'date' && exportCell.value instanceof Date) {
    cell.value = exportCell.value;
    cell.numFmt = REBATE_XLSX_DATE_FORMAT;
    return;
  }
  if (
    (exportCell.kind === 'number' || exportCell.kind === 'currency') &&
    typeof exportCell.value === 'number'
  ) {
    applyNumericFormat(cell, exportCell.value, exportCell.kind === 'currency');
    return;
  }
  cell.value = String(exportCell.value);
}

function writeExportRow(
  worksheet: Worksheet,
  rowNumber: number,
  row: RebateExportRow,
) {
  const excelRow = worksheet.getRow(rowNumber);
  row.cells.forEach((exportCell, index) => {
    const cell = excelRow.getCell(index + 1);
    writeExportCell(cell, exportCell);
    if (row.role === 'header') {
      cell.font = { bold: true };
    }
  });
}

function firstHeaderRowNumber(rows: RebateExportRow[]): number | null {
  const index = rows.findIndex((row) => row.role === 'header');
  return index === -1 ? null : index + 1;
}

export async function writeRebateReportWorkbook(
  rows: RebateExportRow[],
): Promise<ArrayBuffer> {
  const { default: ExcelJS } = await import('exceljs');
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet(REBATE_XLSX_SHEET_NAME);
  rows.forEach((row, index) => {
    writeExportRow(worksheet, index + 1, row);
  });
  const headerRowNumber = firstHeaderRowNumber(rows);
  if (headerRowNumber !== null) {
    worksheet.views = [{ state: 'frozen', ySplit: headerRowNumber }];
  }
  const buffer = await workbook.xlsx.writeBuffer();
  if (buffer instanceof ArrayBuffer) {
    return buffer;
  }
  const view = new Uint8Array(buffer);
  return view.buffer.slice(view.byteOffset, view.byteOffset + view.byteLength);
}
